import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, Globe, Sparkles } from "lucide-react";

const dm = (d) => { try { const [, m, day] = d.split("-"); return `${day}/${m}`; } catch { return d; } };
const FEATURE_LABELS = {
  analisis: "Analisis AI", compare: "Bandingkan", screener: "Screener", robot: "Robot AI",
  briefing: "Briefing", scalping: "Scalping", watchlist: "Watchlist", calculator: "Kalkulator",
  probability: "Probabilitas", calendar: "Kalender", news: "Berita", telegram: "Telegram", membership: "Membership",
};
const featLabel = (n) => FEATURE_LABELS[n] || n;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--surface-card)] px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold mb-1">{dm(label)}</p>
      <p className="text-accent">Kunjungan: {payload[0]?.value}</p>
      {payload[1] && <p className="text-[var(--text-secondary)]">Unik: {payload[1]?.value}</p>}
    </div>
  );
};

export default function AdminAnalytics({ stats }) {
  if (!stats) return null;
  const daily = stats.daily || [];
  const sources = stats.sources || [];
  const feats = stats.top_features || [];
  const srcMax = Math.max(1, ...sources.map((s) => s.count));
  const featMax = Math.max(1, ...feats.map((f) => f.count));

  return (
    <div className="space-y-4 mb-6" data-testid="admin-analytics">
      <div className="card p-4">
        <p className="font-heading font-bold text-sm mb-4 flex items-center gap-2"><TrendingUp size={15} className="text-accent" /> Tren Kunjungan 30 Hari Terakhir</p>
        <div style={{ width: "100%", height: 220 }} data-testid="visits-chart">
          <ResponsiveContainer>
            <LineChart data={daily} margin={{ top: 5, right: 12, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="var(--accent)" stopOpacity={0.2} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="date" tickFormatter={dm} tick={{ fill: "var(--text-muted)", fontSize: 10 }} interval={4} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: "var(--text-muted)", fontSize: 10 }} axisLine={false} tickLine={false} width={40} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="visits" stroke="var(--accent)" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
              <Line type="monotone" dataKey="unique" stroke="var(--text-secondary)" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center gap-4 mt-2 text-[11px] text-[var(--text-muted)]">
          <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[var(--accent)] inline-block" /> Total kunjungan</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 inline-block" style={{ borderTop: "1.5px dashed var(--text-secondary)" }} /> Pengunjung unik</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-4">
          <p className="font-heading font-bold text-sm mb-3 flex items-center gap-2"><Globe size={15} className="text-accent" /> Sumber Kunjungan</p>
          {sources.length === 0 ? <p className="text-sm text-[var(--text-muted)]">Belum ada data.</p> : (
            <div className="space-y-2.5" data-testid="sources-list">
              {sources.map((s) => (
                <div key={s.source}>
                  <div className="flex items-center justify-between text-xs mb-1"><span>{s.source}</span><span className="font-mono-data font-semibold">{s.count}</span></div>
                  <div className="h-1.5 rounded-full bg-[var(--surface-base)] overflow-hidden"><div className="h-full bg-accent rounded-full" style={{ width: `${(s.count / srcMax) * 100}%`, background: "var(--accent)" }} /></div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="card p-4">
          <p className="font-heading font-bold text-sm mb-3 flex items-center gap-2"><Sparkles size={15} className="text-accent" /> Fitur Terpopuler</p>
          {feats.length === 0 ? <p className="text-sm text-[var(--text-muted)]">Belum ada klik fitur tercatat.</p> : (
            <div className="space-y-2.5" data-testid="features-list">
              {feats.map((f) => (
                <div key={f.name}>
                  <div className="flex items-center justify-between text-xs mb-1"><span>{featLabel(f.name)}</span><span className="font-mono-data font-semibold">{f.count}×</span></div>
                  <div className="h-1.5 rounded-full bg-[var(--surface-base)] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(f.count / featMax) * 100}%`, background: "var(--gold)" }} /></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
